import { useTranslations } from '@vocab/react';
import { Divider, Heading, Stack } from 'braid-design-system';
import { useEffect, useRef } from 'react';

import type { ProfileView } from '../../types/profile.types';
import type { LoginPromptProps } from '../GenericLoginPrompt/GenericLoginPrompt';
import { CareerHistory } from '../ProfileView/CareerHistory/CareerHistory';
import { ProfileViewCard } from '../ProfileView/ProfileViewCard/ProfileViewCard';

import translations from './.vocab';
import { SignedOutProfileLoginPrompt } from './SignedOutProfileLoginPrompt/SignedOutProfileLoginPrompt';

interface Props {
  profile: ProfileView;
  authUrls: {
    loginUrl: string;
    googleLoginUrl: string;
    registerUrl: string;
  };
  trackingCallbacks: {
    profileDisplayedCallback: () => void;
  } & LoginPromptProps['trackingCallbacks'];
}

export const SignedOutProfileView = ({
  profile,
  authUrls,
  trackingCallbacks: {
    profileDisplayedCallback,
    onComponentDisplayed,
    onAuthLinkPressed,
    onLoginPressed,
  },
}: Props) => {
  const { t } = useTranslations(translations);
  const hasTrackedDisplayRef = useRef<string | null>(null);

  useEffect(() => {
    const profileKey = profile.profileGuid;
    if (hasTrackedDisplayRef.current !== profileKey) {
      profileDisplayedCallback();
      hasTrackedDisplayRef.current = profileKey;
    }
  }, [profileDisplayedCallback, profile.profileGuid]);

  const loginPromptTrackingCallbacks = {
    onComponentDisplayed,
    onLoginPressed,
    onAuthLinkPressed,
  };

  return (
    <>
      <Stack space="large">
        <ProfileViewCard profile={profile} isSignedIn={false} profileUrl="" />
        <Divider />
        <Heading level="3">{t('CAREER_HISTORY')}</Heading>
        <CareerHistory workHistories={profile.workHistories} />
      </Stack>

      <SignedOutProfileLoginPrompt
        authUrls={authUrls}
        trackingCallbacks={loginPromptTrackingCallbacks}
      />
    </>
  );
};
